<template>
  <div class="weChat-template">
    <div class="weChat-account-info">
      <div class="account-info">
        <div class="account-appid">
          <div class="label">{{ $t(`${i18nPrefix}.label.weChatAccountIdLabel`) }}</div>
          <div class="appid-input">
            <el-input class="input-content" v-model="weChatAppId" @change="handleAccountInfoChange"></el-input>
            <div v-if="showAppIdTip" class="error">{{ $t(`${i18nPrefix}.tip.${invalidAppId ? 'invalidAppId' : 'appIdError'}`) }}</div>
          </div>
        </div>
        <div class="account-appid">
          <div class="label">{{ $t(`${i18nPrefix}.label.weChatAccountKeyLabel`) }}</div>
          <div class="appid-input">
            <el-input class="input-content" v-model="weChatAppSecret" @change="handleAccountInfoChange"></el-input>
            <span v-if="showAppSecretTip" class="error">{{ $t(`${i18nPrefix}.tip.${invalidAppId ? 'invalidAppSecret' : 'appSecretError'}`) }}</span>
          </div>
        </div>
        <div class="account-appid">
          <div class="label">{{ $t(`${i18nPrefix}.label.weChatAccountTokenLabel`) }}</div>
          <div class="appid-input">
            <el-input class="input-content" v-model="weChatAccountToken"></el-input>
          </div>
        </div>
        <div class="account-appid">
          <div class="label">{{ $t(`${i18nPrefix}.label.weChatAccountPasswordLabel`) }}</div>
          <div class="appid-input">
            <el-input class="input-content" v-model="weChatAccountPassword"></el-input>
          </div>
        </div>
        <div v-if="isAppIdSaved && weChatAppId" class="account-appid webhook-path disabled">
          <div class="label">{{ $t(`${i18nPrefix}.label.weChatWebhook`) }}</div>
          <div class="appid-input">
            <el-input v-model="webhookPath" class="input-content" disabled></el-input>
          </div>
        </div>
        <div class="account-appid">
          <div class="label">
            {{ $t(`${i18nPrefix}.label.weChatAccountUrl`) }}
            <el-tooltip
              effect="light"
              popper-class="light-tooltip-popper"
              :content="$t(`${i18nPrefix}.label.weChatAccountUrlTip`)"
            >
              <i class="el-icon-question"></i>
            </el-tooltip>
          </div>
          <div class="appid-input">
            <el-input class="input-content" v-model="weChatAccountUrl"></el-input>
          </div>
        </div>
        <div class="account-appid verify-file">
          <div class="label">{{ $t(`${i18nPrefix}.label.verifyFile`) }}</div>
          <div class="appid-input">
            <input ref="file" class="file-input" type="file" accept="text/plain" @change="onFileSelected"/>
            <div class="file-name">{{ fileInputLabel }}</div>
            <el-button class="file-btn" @click="onSelectFileBtnClick">{{ $t(`${i18nPrefix}.btn.selectFile`) }}</el-button>
          </div>
        </div>
        <div class="save-btn">
          <el-button class="btn" @click="handleSaveWeChatAccountInfo">{{ $t('common.btn.save') }}</el-button>
        </div>
      </div>
    </div>
    <List
      class="template-list"
      titleI18nKey=""
      :loading="loading"
      :listData="companyTemplateTypeList"
      :tableColumns="tableColumns"
      :actionBtn="actionBtn"
      :pagination="pagination"
      :showCreateBtn="false"
      @pagination-change="handlePaginationChange"
    >
      <template #status="{ row }">
        <el-switch
          v-model="row.is_enabled"
          class="status-switch"
          :width="34"
          :disabled="!row.has_template"
          @change="handleSwitch(row)"
        ></el-switch>
      </template>
    </List>
    <DialogWrapper
      class="edit-weChat-template"
      :show.sync="editMessageTemplateDialog.show"
      :closeOnCancel="true"
      :closeOnClickModal="false"
      :destroyOnClose="true"
      :title="editMessageTemplateDialog.title"
      :loading="editMessageTemplateDialog.loading"
      @save="handleSaveMessageTemplate"
    >
      <div class="dialog-tip">{{ $t(`${i18nPrefix}.label.createOrEditTemplateTitle`) }}</div>
      <div class="template-id-title">{{ $t(`${i18nPrefix}.label.createOrEditTemplateIdLabel`) }}</div>
      <el-input class="template-id" v-model="currentTemplateId" @change="handleWeChatTemplateIdChange" :placeholder="$t(`${i18nPrefix}.label.createOrEditTemplateIdLabel`)"></el-input>
      <div v-if="showMustNeedWeChatTemplateId" class="must-need-template-id">{{ $t(`${i18nPrefix}.tip.mustNeedTemplateId`) }}</div>
      <div class="template-content">
        <div class="content-title">{{ $t(`${i18nPrefix}.label.createOrEditTemplateFieldContent`) }}</div>
        <div class="content">
          <div class="template-fields" v-for="(field, selectIndex) in templateSelectFieldList" :key="selectIndex">
            <div class="field-type">
              <el-select
                v-if="!field.is_custom"
                v-model="field.type_field_value"
                filterable
                clearable
                class="template-fields-select"
                :placeholder="$t(`${i18nPrefix}.label.selectTemplateField`)"
                @change="handleTypeFieldChange($event, selectIndex)">
                <el-option
                  v-for="(item, index) in templateFieldList"
                  :key="index"
                  :label="item.i18nName"
                  :value="item.value"
                ></el-option>
              </el-select>
              <el-autocomplete
                v-if="field.is_custom"
                clearable
                class="inline-input"
                v-model="field.type_field_value"
                :fetch-suggestions="querySearchTypeField"
                :placeholder="$t(`${i18nPrefix}.label.customTemplateTypeFieldLabel`)"
                @select="handleAutocompleteTypeFieldSelect($event, selectIndex)"
                @change="handleAutocompleteTypeFieldChange($event, selectIndex)"
              >
                <template slot="prepend">{{ $t(`${i18nPrefix}.label.customTemplateTypeFieldValue`) }}</template>
              </el-autocomplete>
              <span class="validate-required" v-if="field.showTypeValueTip">{{ $t('common.message.requiredFormField') }}</span>
            </div>
            <div class="custom-field">
              <el-input class="select-content" v-model="field.custom_field_value" :placeholder="$t(`${i18nPrefix}.label.inputCustomField`)" @change="handleCustomFieldValueChange(field)"></el-input>
              <span class="validate-required" v-if="field.showCustomFieldValueTip">{{ $t('common.message.requiredFormField') }}</span>
            </div>
            <div class="btn-group">
              <el-button v-if="selectIndex === templateSelectFieldList.length - 1" type="text" class="add-btn" @click="handleAddTemplateField">
                <i class="icomoon-add-box icon-btn"></i>
              </el-button>
                <el-button v-else type="text" class="delete-btn" @click="handleDeleteTemplate(field, selectIndex)">
                  <i class="icomoon-remove-box icon-btn"></i>
                </el-button>
            </div>
          </div>
        </div>
      </div>
      <div class="content-preview">
        <div class="content-preview-title">{{ $t(`${i18nPrefix}.label.createOrEditTemplatePreview`) }}</div>
        <el-input v-model="templatePreviewContent" type="textarea" class="wechat-template-content" :rows="templatePreviewContentRow" disabled :placeholder="editMessageTemplateDialog.title"></el-input>
      </div>
    </DialogWrapper>
    <ConfirmDialog
      class="confirm-delete-dialog"
      :title="$t(`${i18nPrefix}.tip.createOrUpdateAppAccount`)"
      :show.sync="showAppAccountConfirmDialog"
      :content="hasCompanyWeChatAccount ? $t(`${i18nPrefix}.message.confirmUpdateAppAccountTitle`) : $t(`${i18nPrefix}.message.confirmCreateAppAccountTitle`)"
      @confirm="saveWeChatAccountInfo"
    >
    </ConfirmDialog>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { addI18nProperty } from '@/i18n'
import List from '@/components/tableList/listTableHasPage.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import ConfirmDialog from '@/components/dialog/confirmDialog.vue'

const MAX_VERIFY_FILE_SIZE = 10 * 1024 // 10 KB

export default {
  components: {
    List,
    DialogWrapper,
    ConfirmDialog
  },
  data () {
    return {
      i18nPrefix: 'admin.company.weChatTemplate',
      isAppIdSaved: false,
      weChatAppId: null,
      weChatAppSecret: null,
      weChatAccountToken: null,
      weChatAccountPassword: null,
      weChatAccountUrl: null,
      verifyFile: null,
      verifyFileName: null,
      hasCompanyWeChatAccount: false,
      loading: false,
      showAppIdTip: false,
      showAppSecretTip: false,
      showMustNeedWeChatTemplateId: false,
      showAppAccountConfirmDialog: false,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      companyTemplateTypeList: [],
      tableColumns: [
        {
          width: 100,
          labelI18nKey: 'admin.company.weChatTemplate.label.weChatTemplateMessageTitle',
          dataKey: 'i18nName'
        },
        {
          width: 65,
          labelI18nKey: 'admin.company.weChatTemplate.label.weChatTemplateMessageStatus',
          slotName: 'status',
          dataKey: 'is_enabled'
        }
      ],
      actionBtn: {
        edit: {
          show: (row) => {
            return !row.is_default
          },
          onclick: this.handleOpenEditDialog
        }
      },
      editMessageTemplateDialog: {
        show: false,
        title: null,
        loading: false,
        templateTypeId: null
      },
      currentTemplateId: null,
      templateSelectFieldList: [
        {
          type_field_value: null,
          custom_field_value: null,
          is_custom: false,
          showTypeValueTip: false,
          showCustomFieldValueTip: false
        }
      ],
      templateFieldList: [],
      typeFieldList: [],
      templatePreviewContent: null,
      templatePreviewContentRow: 3,
      invalidAppId: false
    }
  },
  computed: {
    webhookPath () {
      const currentAccount = this.getCurrentAccount()
      const companyId = currentAccount.company_id
      return `${window.location.origin}/wechat/webhook/${companyId}/${this.weChatAppId}/`
    },
    fileInputLabel () {
      if (this.verifyFileName) {
        return this.verifyFileName
      } else {
        return this.$t(`${this.i18nPrefix}.placeholder.verifyFile`)
      }
    }
  },
  created () {
    this.loadCompanyWeChatTemplateList()
    this.loadCompanyWeChatTemplateAppAccountInfo()
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('wechat', [
      'getCompanyWeChatTemplateMessageList',
      'getCompanyWeChatTemplateAppAccount',
      'getWeChatTemplateMessageInfo',
      'createOrUpdateWeChatTemplateAppAccount',
      'updateWeChatTemplateMessageStatus',
      'createOrUpdateWeChatTemplateMessage',
      'getCompanyWeChatAccountTemplateInfoByTemplateId'
    ]),
    querySearchTypeField (keyword, callback) {
      let results = []
      const templateFieldList = _.filter(this.templateFieldList, item => item.value !== 'custom')
      if (!keyword) {
        results = templateFieldList
      } else {
        results = _.filter(templateFieldList, item => _.includes(item.i18nName.toUpperCase(), keyword.toUpperCase()))
      }
      callback(_.map(results, val => { return { value: val.i18nName, valueKey: val.value } }))
    },
    async loadCompanyWeChatTemplateList () {
      this.loading = true
      const params = {
        page: this.pagination.page,
        page_size: this.pagination.pageSize
      }
      try {
        const resp = await this.getCompanyWeChatTemplateMessageList(params)
        if (resp) {
          const companyTemplateTypeList = resp.company_template_type_list
          _.forEach(companyTemplateTypeList, item => {
            addI18nProperty(item, 'i18nName', 'cn_name', 'name')
          })
          this.companyTemplateTypeList = companyTemplateTypeList
          this.pagination.total = resp.total_count
        }
      } finally {
        this.loading = false
      }
    },
    async loadCompanyWeChatTemplateAppAccountInfo () {
      const resp = await this.getCompanyWeChatTemplateAppAccount()
      if (resp) {
        this.weChatAppId = resp.app_id
        this.weChatAppSecret = resp.app_secret
        this.weChatAccountToken = resp.webhook_token
        this.weChatAccountPassword = resp.webhook_encoding_aes_key
        this.weChatAccountUrl = resp.recruitment_url
        this.verifyFileName = resp.verify_file_name
        this.verifyFile = null
        this.hasCompanyWeChatAccount = resp.has_company_wechat_account
        if (resp.app_id) {
          this.isAppIdSaved = true
        }
      }
    },
    handleAccountInfoChange () {
      this.invalidAppId = false
      if (!this.weChatAppId) {
        this.showAppIdTip = true
      } else {
        this.showAppIdTip = false
      }
      if (!this.weChatAppSecret) {
        this.showAppSecretTip = true
      } else {
        this.showAppSecretTip = false
      }
    },
    onSelectFileBtnClick () {
      this.$refs.file.click()
    },
    onFileSelected () {
      const el = this.$refs.file
      const file = el.files[0]
      if (file.size > MAX_VERIFY_FILE_SIZE) {
        this.$message({
          type: 'error',
          message: this.$t(`${this.i18nPrefix}.message.verifyFileSize`, { size: parseInt(MAX_VERIFY_FILE_SIZE / 1024) })
        })
        return
      }
      this.verifyFileName = file.name
      this.verifyFile = file
    },
    async handleSaveWeChatAccountInfo () {
      if (!this.weChatAppId) {
        this.showAppIdTip = true
        return
      }
      if (!this.weChatAppSecret) {
        this.showAppSecretTip = true
        return
      }
      this.showAppIdTip = false
      this.showAppSecretTip = false
      this.showAppAccountConfirmDialog = true
    },
    async saveWeChatAccountInfo () {
      this.$eventBus.$emit('showLoading', true)
      try {
        const params = {
          app_id: this.weChatAppId,
          app_secret: this.weChatAppSecret,
          webhook_token: this.weChatAccountToken,
          webhook_encoding_aes_key: this.weChatAccountPassword,
          recruitment_url: this.weChatAccountUrl,
          verify_file_name: this.verifyFileName,
          verify_file: this.verifyFile
        }
        const resp = await this.createOrUpdateWeChatTemplateAppAccount(params)
        if (resp) {
          this.showAppAccountConfirmDialog = false
          if (resp.invalid_app_id) {
            this.$message({
              type: 'error',
              message: this.$t(`${this.i18nPrefix}.message.invalidAppId`),
              showClose: true
            })
            this.invalidAppId = true
          } else {
            this.$message({
              type: 'success',
              message: this.hasCompanyWeChatAccount ? this.$t(`${this.i18nPrefix}.message.updateAppAccountSuccess`) : this.$t(`${this.i18nPrefix}.message.createAppAccountSuccess`)
            })
            this.isAppIdSaved = true
            this.invalidAppId = false
            this.verifyFile = null
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handlePaginationChange () {
      this.loadCompanyWeChatTemplateList()
    },
    handleAddTemplateField () {
      this.templateSelectFieldList.push({ custom_field_value: null, type_field_value: null, is_custom: false, showTypeValueTip: false, showCustomFieldValueTip: false })
    },
    handleDeleteTemplate (field, index) {
      this.templateSelectFieldList.splice(index, 1)
    },
    async handleOpenEditDialog (row, index) {
      this.templatePreviewContent = null
      this.templateSelectFieldList = []
      this.templatePreviewContentRow = 3
      this.showMustNeedWeChatTemplateId = false
      this.editMessageTemplateDialog.templateTypeId = row.id
      this.editMessageTemplateDialog.title = row.i18nName
      this.editMessageTemplateDialog.loading = true
      this.editMessageTemplateDialog.show = true
      try {
        const params = {
          template_type_id: row.id
        }
        const resp = await this.getWeChatTemplateMessageInfo(params)
        if (resp) {
          this.currentTemplateId = resp.template_id
          const templateSelectFieldMap = resp.parameter
          const templateSelectFieldList = []
          _.forEach(templateSelectFieldMap, (val, key) => {
            templateSelectFieldList.push(...val)
          })
          this.templateSelectFieldList.push(..._.map(templateSelectFieldList, (item) => {
            const field = {
              type_field_value: null,
              custom_field_value: null,
              is_custom: false
            }
            _.forEach(item, (val, key) => {
              field.type_field_value = key
              field.custom_field_value = val.wechat_template_field
              field.is_custom = val.is_custom
            })
            return field
          }))
          this.templateSelectFieldList.push({
            type_field_value: null,
            custom_field_value: null,
            is_custom: false,
            showTypeValueTip: false,
            showCustomFieldValueTip: false
          })
          const templateFieldList = resp.all_template_type_field_list
          _.forEach(templateFieldList, item => {
            addI18nProperty(item, 'i18nName', 'cn_name', 'name')
            this.typeFieldList.push(item.value)
          })
          this.templateFieldList = templateFieldList
        }
        if (this.currentTemplateId) {
          await this.loadTemplateInfoById(this.currentTemplateId)
        }
      } finally {
        this.editMessageTemplateDialog.loading = false
      }
    },
    async handleSwitch (rowData) {
      this.editMessageTemplateDialog.loading = true
      try {
        const params = {
          template_type_id: rowData.id,
          is_enabled: rowData.is_enabled
        }
        await this.updateWeChatTemplateMessageStatus(params)
      } finally {
        this.editMessageTemplateDialog.loading = false
      }
    },
    handleCustomFieldValueChange (field) {
      if (field.custom_field_value) {
        this.$set(field, 'showCustomFieldValueTip', false)
      } else {
        this.$set(field, 'showCustomFieldValueTip', true)
      }
    },
    handleAutocompleteTypeFieldChange (inputValue, selectIndex) {
      if (!inputValue) {
        this.$set(this.templateSelectFieldList[selectIndex], 'is_custom', false)
      }
    },
    handleAutocompleteTypeFieldSelect (inputValue, selectIndex) {
      const selectValue = inputValue.valueKey
      if (selectValue) {
        this.$set(this.templateSelectFieldList[selectIndex], 'showTypeValueTip', false)
      } else {
        this.$set(this.templateSelectFieldList[selectIndex], 'showTypeValueTip', true)
      }
      if (selectValue === 'custom') {
        this.$set(this.templateSelectFieldList[selectIndex], 'is_custom', true)
        this.$set(this.templateSelectFieldList[selectIndex], 'type_field_value', null)
      } else if (_.includes(this.typeFieldList, selectValue)) {
        const field = _.find(this.templateFieldList, item => item.value === selectValue)
        this.$set(this.templateSelectFieldList[selectIndex], 'is_custom', false)
        this.$set(this.templateSelectFieldList[selectIndex], 'type_field_value', field.value)
      }
    },
    handleTypeFieldChange (selectValue, selectIndex) {
      if (selectValue) {
        this.$set(this.templateSelectFieldList[selectIndex], 'showTypeValueTip', false)
      } else {
        this.$set(this.templateSelectFieldList[selectIndex], 'showTypeValueTip', true)
      }
      if (selectValue === 'custom') {
        this.$set(this.templateSelectFieldList[selectIndex], 'is_custom', true)
        this.$set(this.templateSelectFieldList[selectIndex], 'type_field_value', null)
      } else if (_.includes(this.typeFieldList, selectValue)) {
        this.$set(this.templateSelectFieldList[selectIndex], 'is_custom', false)
      }
    },
    async handleWeChatTemplateIdChange () {
      if (!this.currentTemplateId) {
        this.showMustNeedWeChatTemplateId = true
      } else {
        this.showMustNeedWeChatTemplateId = false
        await this.loadTemplateInfoById(this.currentTemplateId)
      }
    },
    async loadTemplateInfoById (templateId) {
      const resp = await this.getCompanyWeChatAccountTemplateInfoByTemplateId({ templateId: templateId })
      if (resp) {
        this.templatePreviewContent = resp
        const regex = /\n/igm
        this.templatePreviewContentRow = this.templatePreviewContent.match(regex).length + 1
      }
    },
    async handleSaveMessageTemplate () {
      if (!this.currentTemplateId) {
        this.showMustNeedWeChatTemplateId = true
        return
      } else {
        this.showMustNeedWeChatTemplateId = false
      }
      let validate = true
      const index = this.templateSelectFieldList.length - 1
      if (index > 0) {
        const tmpField = this.templateSelectFieldList[index]
        if (!tmpField.type_field_value && !tmpField.custom_field_value) {
          this.templateSelectFieldList.splice(index, 1)
        }
      }
      _.forEach(this.templateSelectFieldList, item => {
        if (!item.type_field_value) {
          item.showTypeValueTip = true
          validate = false
        }
        if (!item.custom_field_value) {
          item.showCustomFieldValueTip = true
          validate = false
        }
      })
      if (!validate) {
        return
      }
      const params = {
        template_type_id: this.editMessageTemplateDialog.templateTypeId,
        wechat_template_id: this.currentTemplateId,
        template_type_field_list: this.templateSelectFieldList
      }
      this.editMessageTemplateDialog.loading = true
      try {
        await this.createOrUpdateWeChatTemplateMessage(params)
      } finally {
        this.editMessageTemplateDialog.loading = false
        this.editMessageTemplateDialog.show = false
        this.loadCompanyWeChatTemplateList()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.weChat-template {
  overflow: hidden;
  background: #FFFFFF;
  padding: 28px 24px 0 24px;
  min-height: 100%;

  .weChat-account-info {
    .account-info {
      .account-appid {
        display: flex;
        align-items: center;
        margin-bottom: 14px;
        .label {
          flex: 1;
          color: #606266;
          word-break: break-word;
          white-space: normal;
        }

        .appid-input {
          flex: 2;
          .input-content {
          }

          .error {
            margin-top: 10px;
            color: #f56c6c;
            font-size: 12px;
          }
        }

        &.webhook-path {
          &.disabled {
            ::v-deep .el-input__inner {
              cursor: text;
            }
          }
        }

        &.verify-file {
          .file-input {
            display: none;
          }

          .appid-input {
            display: flex;

            .file-name {
              flex: 1;
              line-height: 32px;
            }
            .file-btn {
              flex-shrink: 0;
              margin-left: 10px;
              margin-right: 0;
              padding: 8px 14px;
              border-color: #0096cc;
              color: #0096cc;
            }
          }
        }
      }

      .save-btn {
        text-align: right;
        .btn {
          padding: 6px 14px;
          border-radius: 2px;
          border-color: #0096cc;
          color: #0096cc;
          line-height: 18px;
        }
      }
    }
  }

  .template-list {
  }
}

.edit-weChat-template {
  .dialog-tip {
    margin-bottom: 14px;
    font-size: 18px;
    font-weight: 500;
    color: #606266;
  }

  .template-id-title {
    margin-bottom: 16px;
    color: #606266;
    font-size: 14px;
    font-weight: 400;
  }

  .template-id {
    margin-bottom: 18px;
  }

  .must-need-template-id {
    color: #f56c6c;
    font-size: 12px;
    margin-bottom: 20px;
  }

  .start-end-fields {
    margin-bottom: 20px;
    font-size: 14px;
    font-weight: 500;
    color: #606266;
  }

  .template-content {
    .content-title {
      margin-bottom: 10px;
      font-size: 14px;
      font-weight: 400;
      color: #606266;
    }

    .content {
      display: inline-block;
      width: 100%;

      .template-fields {
        display: flex;
        margin-bottom: 20px;

        .field-type {
          margin-right: 10px;
          flex: 1;
          float: left;
          .template-fields-select {
          }

          .validate-required {
            color: #f56c6c;
            font-size: 12px;
          }
        }

        .custom-field {
          flex: 1;
          float: left;
          margin-left: 10px;
          .select-content {
          }

          .validate-required {
            color: #f56c6c;
            font-size: 12px;
          }
        }

        .btn-group {
          float: left;
          margin-left: 14px;
          .icon-btn {
            font-size: 30px;

            &::before {
              color: #0096cc;
            }
          }
          .add-btn {
            height: 32px;
            display: flex;
            align-items: center;
            margin-top: 1px;
          }

          .delete-btn {
            height: 32px;
            display: flex;
            align-items: center;
            margin-top: 1px;
          }
        }
      }
    }
  }

  .content-preview {
    margin-bottom: 20px;
    .content-preview-title {
      margin-bottom: 10px;
      font-size: 14px;
      font-weight: 400;
      color: #606266;
    }

    .wechat-template-content {

      ::v-deep {
        .el-textarea__inner {
          cursor: pointer;
        }
      }

    }

  }
}
</style>
